import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSortBy, useTable } from "react-table";
import currency from "currency.js";
import { ethers } from "ethers";
import Select from "antd/es/select";
import { DataContext } from "../../context/DataContext";
import {
  useTradingRewardsV2Epoch,
  ITradingRewardsV2AccountData,
} from "../../hooks/api/staking/useTradingRewardsV2";
import { getCurrentEpoch } from "../../hooks/api/staking/useStakeInfo";
import {
  useTradingRewardsHistory,
  ITradeRewardRecord,
} from "../../hooks/api/staking/useTradingRewardsHistory";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import {
  Align,
  CopyButton,
  DashboardRow,
  TableHeaderCell,
  TableWrapper,
  TitleWrapper,
} from "../Table/style";
import { ITableColumn } from "../Table/TableColumn";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import { PageEndpointEnum } from "../../enums/endpoints";
import { Spinner } from "../shared/Spinner";
import { LinkText } from "../Dashboard/style";
import { SPACING } from "../../constants/design/spacing";
import { useStoreTradingEpochRewards } from "../../hooks/api/staking/useStoreTradingEpochRewards";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import ConfirmationModal from "../shared/ConfirmationModal";
import { FONT_STYLE } from "../../constants/design/fontSize";

export function TradingRewardsInfo() {
  const { records } = useContext(DataContext);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { storeTradingEpochRewards, isLoading: isStoringTradingRewards } =
    useStoreTradingEpochRewards();

  // Default to current epoch (same as Staking Info)
  const currentEpoch = getCurrentEpoch();
  const [selectedEpoch, setSelectedEpoch] = useState<number | undefined>(
    currentEpoch
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmedOverwrite, setConfirmedOverwrite] = useState(false);

  // Generate valid epochs: 1 to currentEpoch (same as Staking Info)
  const validEpochs = useMemo(
    () => Array.from({ length: currentEpoch }, (_, i) => i + 1).reverse(),
    [currentEpoch]
  );

  // Fetch trade history (stored rewards)
  const { data: tradeHistory, isLoading: isTradeHistoryLoading } =
    useTradingRewardsHistory();

  // Fetch preview data for the selected epoch (only if not in history)
  const { data: previewEpochData, isLoading: isPreviewLoading } =
    useTradingRewardsV2Epoch(selectedEpoch);

  // Check if the selected epoch exists in trade history
  const selectedEpochHistoryData = useMemo(() => {
    if (!tradeHistory || !selectedEpoch) return null;
    return tradeHistory.filter((record) => record.Epoch === selectedEpoch);
  }, [tradeHistory, selectedEpoch]);

  // Convert trade history records to ITradingRewardsV2AccountData format
  const historyEpochData = useMemo((): ITradingRewardsV2AccountData[] => {
    if (!selectedEpochHistoryData || selectedEpochHistoryData.length === 0) {
      return [];
    }
    return selectedEpochHistoryData.map((record) => ({
      account: record.Account,
      epoch: record.Epoch,
      trading_volume: record.TradingVolume,
      percentage_share: record.PercentageShare,
      reward_amount: record.RewardAmount,
    }));
  }, [selectedEpochHistoryData]);

  // Use history data if available, otherwise use preview data
  const epochData = useMemo(() => {
    if (historyEpochData.length > 0) {
      return historyEpochData;
    }
    return previewEpochData || [];
  }, [historyEpochData, previewEpochData]);

  const isLoading = isTradeHistoryLoading || isPreviewLoading;

  // Check if trading rewards already exist for the selected epoch (in history)
  const hasExistingRewardsForSelectedEpoch = useMemo(() => {
    if (!selectedEpoch) return false;
    return historyEpochData.length > 0;
  }, [historyEpochData, selectedEpoch]);

  // Check if the selected epoch hasn't ended yet (can only store after epoch has ended)
  // Selected epoch can only be stored if currentEpoch > selectedEpoch
  const isSelectedEpochActive = useMemo(() => {
    if (!selectedEpoch) return false;
    // Can only store if current epoch is greater than selected epoch
    // (meaning the selected epoch has ended)
    return currentEpoch <= selectedEpoch;
  }, [selectedEpoch, currentEpoch]);

  const onCopy = useCallback(
    (address: string) => {
      navigator.clipboard.writeText(address);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Address Copied",
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  // Convert address to checksum format for display
  const getChecksumAddress = useCallback((address: string) => {
    try {
      return ethers.utils.getAddress(address);
    } catch {
      return address; // Return as-is if not a valid address
    }
  }, []);

  const getAirtableTag = useCallback(
    (address: string) => {
      // Check both checksummed and lowercase versions
      const checksummed = getChecksumAddress(address);
      const lowercased = address.toLowerCase();
      return (
        records[checksummed] ||
        records[lowercased] ||
        records[address] ||
        undefined
      );
    },
    [records, getChecksumAddress]
  );

  // Format volume
  const formatVolume = useCallback((volume: number) => currency(volume, { precision: 2 }).format({ symbol: "$" }), []);

  // Format reward amount from wei (18 decimals)
  const formatRewardAmount = useCallback((amount: string) => {
    const amountInToken = Number(amount) / 1e18;
    return currency(amountInToken, { precision: 2 }).format({ symbol: "" });
  }, []);

  // Format percentage
  const formatPercentage = useCallback((percentage: number) => `${(percentage * 100).toFixed(4)}%`, []);

  // Handler for storing trading epoch rewards
  const handleStoreTradingEpochRewards = useCallback(async () => {
    const success = await storeTradingEpochRewards();
    if (success) {
      setShowConfirmModal(false);
      setConfirmedOverwrite(false);
    }
  }, [storeTradingEpochRewards]);

  const handleStoreTradingEpochRewardsClick = useCallback(() => {
    if (!selectedEpoch) {
      return;
    }

    // If selected epoch is still active, don't allow storing
    if (isSelectedEpochActive) {
      return;
    }

    // If rewards already exist for selected epoch, don't allow storing (button should be disabled)
    if (hasExistingRewardsForSelectedEpoch) {
      return;
    }

    // Otherwise, proceed directly
    handleStoreTradingEpochRewards();
  }, [
    selectedEpoch,
    isSelectedEpochActive,
    hasExistingRewardsForSelectedEpoch,
    handleStoreTradingEpochRewards,
  ]);

  // Calculate summary from epoch data
  const epochSummary = useMemo(() => {
    if (!epochData || epochData.length === 0) return null;
    const totalAccounts = epochData.length;
    const totalVolume = epochData.reduce(
      (sum, item) => sum + item.trading_volume,
      0
    );
    const totalRewards = epochData.reduce(
      (sum, item) => sum + Number(item.reward_amount),
      0
    );
    return {
      totalAccounts,
      totalVolume,
      totalRewards: totalRewards.toString(),
    };
  }, [epochData]);

  const columns: ITableColumn<ITradingRewardsV2AccountData>[] = useMemo(
    () => [
      {
        id: "account",
        title: "Account Address",
        align: "left",
        accessor: "account",
        Cell: ({ row }: any) => {
          const checksummedAddress = getChecksumAddress(row.original.account);
          const airtableTag = getAirtableTag(row.original.account);
          return (
            <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <CopyButton
                aria-label={`Copy ${checksummedAddress}`}
                style={{ flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy(checksummedAddress);
                }}
              >
                <Copy />
              </CopyButton>
              <LinkText
                title={
                  airtableTag
                    ? `${airtableTag}\n${checksummedAddress}`
                    : checksummedAddress
                }
                style={{
                  color: airtableTag ? COLORS.blue.one : TEXT_COLORS.one,
                  display: "block",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {airtableTag || checksummedAddress}
              </LinkText>
            </div>
          );
        },
      },
      {
        title: "Trading Volume",
        align: "right",
        accessor: "trading_volume",
        Cell: ({ value }: any) => (
          <Align align="right">{formatVolume(value)}</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const volumeA = Number(a.original.trading_volume);
          const volumeB = Number(b.original.trading_volume);
          if (volumeA > volumeB) return 1;
          if (volumeA < volumeB) return -1;
          return 0;
        },
      },
      {
        title: "Percentage Share",
        align: "right",
        accessor: "percentage_share",
        Cell: ({ value }: any) => (
          <Align align="right">{formatPercentage(value)}</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const shareA = Number(a.original.percentage_share);
          const shareB = Number(b.original.percentage_share);
          if (shareA > shareB) return 1;
          if (shareA < shareB) return -1;
          return 0;
        },
      },
      {
        title: "Reward Amount",
        align: "right",
        accessor: "reward_amount",
        Cell: ({ value }: any) => (
          <Align align="right">{formatRewardAmount(value)} AEVO</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const rewardA = Number(a.original.reward_amount);
          const rewardB = Number(b.original.reward_amount);
          if (rewardA > rewardB) return 1;
          if (rewardA < rewardB) return -1;
          return 0;
        },
      },
    ],
    [
      onCopy,
      formatVolume,
      formatPercentage,
      formatRewardAmount,
      getChecksumAddress,
      getAirtableTag,
    ]
  );

  const memoizedEpochData = useMemo(() => epochData || [], [epochData]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: memoizedEpochData,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        initialState: {
          sortBy: [
            {
              id: "reward_amount",
              desc: true,
            },
          ],
        },
        autoResetSortBy: false,
      },
      useSortBy
    );

  return (
    <>
      <TitleWrapper>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <h3>
            Total Traders{" "}
            {memoizedEpochData.length
              ? `(${memoizedEpochData.length})`
              : "(None)"}
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: SPACING.two,
            }}
          >
            <span style={{ marginRight: SPACING.one }}>Epoch:</span>
            <Select
              value={selectedEpoch ?? null}
              onChange={(value) => setSelectedEpoch(value ?? undefined)}
              style={{ width: "120px" }}
              placeholder="Select Epoch"
              loading={isLoading}
              allowClear
            >
              {validEpochs.map((epoch) => (
                <Select.Option key={epoch} value={epoch}>
                  {epoch}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </TitleWrapper>

      {selectedEpoch && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: SPACING.two,
            marginBottom: SPACING.three,
          }}
        >
          {epochSummary && (
            <div
              style={{
                display: "flex",
                gap: SPACING.four,
                color: TEXT_COLORS.one,
              }}
            >
              <div>
                <strong>Total Accounts:</strong> {epochSummary.totalAccounts}
              </div>
              <div>
                <strong>Total Volume:</strong>{" "}
                {formatVolume(epochSummary.totalVolume)}
              </div>
              <div>
                <strong>Total Rewards:</strong>{" "}
                {formatRewardAmount(epochSummary.totalRewards)} AEVO
              </div>
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: SPACING.two,
            }}
          >
            {isSelectedEpochActive && selectedEpoch && (
              <span style={{ color: COLORS.orange.one, fontSize: "14px" }}>
                Epoch {selectedEpoch} hasn&apos;t ended yet
              </span>
            )}
            {!isSelectedEpochActive &&
              hasExistingRewardsForSelectedEpoch &&
              selectedEpoch && (
                <span style={{ color: COLORS.orange.one, fontSize: "14px" }}>
                  Rewards for epoch {selectedEpoch} are already stored
                </span>
              )}
            <Button
              buttonTheme={ButtonThemeEnum.HIGHLIGHT}
              onClick={handleStoreTradingEpochRewardsClick}
              disabled={
                isStoringTradingRewards ||
                isSelectedEpochActive ||
                hasExistingRewardsForSelectedEpoch ||
                !selectedEpoch
              }
            >
              {isStoringTradingRewards
                ? "Storing..."
                : "Store Trading Epoch Rewards"}
            </Button>
          </div>
        </div>
      )}

      <ConfirmationModal
        primaryColor={COLORS.orange.one}
        title="Overwrite Existing Trading Rewards?"
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setConfirmedOverwrite(false);
        }}
        extras={
          <div style={{ marginBottom: SPACING.three }}>
            <div style={FONT_STYLE.label.one}>
              Trading rewards are already allocated for epoch {selectedEpoch}. Are
              you sure you want to overwrite them?
            </div>
            {epochData && epochData.length > 0 && (
            <div
              style={{
                      marginTop: SPACING.two,
                      padding: SPACING.two,
                      backgroundColor: COLORS.neutral.three,
                      borderRadius: "4px",
                    }}
            >
              <div style={FONT_STYLE.label.two}>
                <strong>Preview:</strong> {epochData.length} account(s) with{" "}
                {formatRewardAmount(
                        epochData
                          .reduce(
                            (sum, item) => sum + Number(item.reward_amount),
                            0
                          )
                          .toString()
                      )}{" "}
                AEVO total rewards
              </div>
            </div>
                )}
            <div
              style={{
                display: "flex",
                gap: SPACING.two,
                marginTop: SPACING.three,
              }}
            >
              <Button
                buttonTheme={
                  confirmedOverwrite
                    ? ButtonThemeEnum.POSITIVE
                    : ButtonThemeEnum.NEUTRAL2
                }
                onClick={() => setConfirmedOverwrite(true)}
                style={{ flex: 1 }}
              >
                Yes
              </Button>
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmedOverwrite(false);
                }}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
            </div>
          </div>
        }
        confirmationButton={{
          title: "Store Trading Epoch Rewards",
          onClick: handleStoreTradingEpochRewards,
          disabled: !confirmedOverwrite || isStoringTradingRewards,
        }}
      />

      <TableWrapper>
        {isLoading ? <Spinner /> : null}
        <table
          {...getTableProps()}
          style={{ width: "100%", minWidth: 760, tableLayout: "fixed" }}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line react/jsx-key
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  // eslint-disable-next-line react/jsx-key
                  <TableHeaderCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{ width: column.id === "account" ? "40%" : "20%" }}
                  >
                    {column.render("Header")}
                  </TableHeaderCell>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const { ...rowProps } = row.getRowProps();
              return (
                // eslint-disable-next-line react/jsx-key
                <DashboardRow
                  {...rowProps}
                  onClick={() => {
                    const checksummedAddress = getChecksumAddress(
                      row.original.account
                    );
                    navigate(
                      `${PageEndpointEnum.ACCOUNT}/${checksummedAddress}`
                    );
                  }}
                  hasRecord={!!getAirtableTag(row.original.account)}
                >
                  {row.cells.map((cell) => (
                    // eslint-disable-next-line react/jsx-key
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </DashboardRow>
              );
            })}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  No trading rewards found for epoch {selectedEpoch}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>
    </>
  );
}
